import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { API_URL, PLANS, BETA_PRICING_ACTIVE, sb } from '../utils/config'
import { trackInitiateCheckout } from '../utils/metaPixel'
import merlinImg from '../assets/merlin.png'
import { Button, Sparkles } from './ui'

async function getAuthHeaders() {
  const { data } = await sb.auth.getSession()
  const token = data?.session?.access_token
  return token
    ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' }
}

const PLAN_ORDER = { free: 0, basic: 1, createur: 2, pro: 3 }

export default function PricingModal({ open, onClose }) {
  const { plan: currentPlan, remainingCredits, maxCredits, isUnlimited } = useAuth()
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState(null)
  const [freeBlocked, setFreeBlocked] = useState(false)
  // Toggle billing : 'annual' (-30% par défaut, ancrage économies) ou 'monthly'
  const [billingPeriod, setBillingPeriod] = useState('annual')

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', handler)
    // Check if free plan is blocked during beta (admin toggle)
    fetch(`${API_URL}/api/beta-block-free`).then(r => r.json()).then(d => setFreeBlocked(!!d.blocked)).catch(() => {})
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const currentIdx = PLAN_ORDER[currentPlan] ?? 0

  // Pendant la beta, l'admin peut cacher le plan "free" de la liste d'upgrade
  // SAUF si l'user est deja sur free (il voit son plan actuel).
  const visiblePlans = PLANS.filter(p => {
    if (p.id !== 'free') return true
    if (currentPlan === 'free') return true // show so they know it's their current plan
    return !freeBlocked
  })

  async function handleUpgrade(targetPlan) {
    setLoading(targetPlan)
    setError(null)
    try {
      trackInitiateCheckout({ plan: targetPlan, billingPeriod })
      const headers = await getAuthHeaders()
      const res = await fetch(`${API_URL}/api/stripe/checkout`, {
        method: 'POST', headers, body: JSON.stringify({ plan: targetPlan, billingPeriod }),
      })
      const data = await res.json()
      if (data.url) window.location.href = data.url
      else { setError(data.error || 'Erreur lors de la création de la session'); setLoading(null) }
    } catch (e) { setError(e.message); setLoading(null) }
  }

  async function handleManageBilling() {
    setLoading('portal')
    setError(null)
    try {
      const headers = await getAuthHeaders()
      const res = await fetch(`${API_URL}/api/stripe/portal`, { method: 'POST', headers })
      const data = await res.json()
      if (data.url) window.location.href = data.url
      else { setError(data.error || 'Erreur'); setLoading(null) }
    } catch (e) { setError(e.message); setLoading(null) }
  }

  return (
    <div className="fixed inset-0 z-[1010] flex items-center justify-center sm:p-4 overflow-y-auto" onClick={onClose}>
      {/* Backdrop — night ambience, solid enough to hide banners behind */}
      <div className="fixed inset-0 animate-fade"
        style={{ background: 'linear-gradient(135deg, rgba(10,6,18,0.96) 0%, rgba(20,12,48,0.96) 50%, rgba(35,20,82,0.96) 100%)', backdropFilter: 'blur(12px)' }} />

      {/* Modal — night theme, full-screen on mobile, centered sheet on desktop */}
      <div
        className="relative w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[92vh] overflow-hidden sm:rounded-2xl animate-in z-10"
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1e1145 0%, #2d1b69 40%, #3b1f8e 100%)',
          border: '1px solid rgba(254,130,4,0.25)',
          boxShadow: '0 24px 80px rgba(15,10,26,0.6), 0 0 60px rgba(254,130,4,0.1)',
        }}
      >
        {/* Sparkles decoration */}
        <Sparkles count={10} color="#fe8204" />

        {/* Close */}
        <button onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 z-20"
          style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)' }}
          aria-label="Fermer">✕</button>

        {/* Header */}
        <div className="relative px-6 pt-7 pb-5 text-center z-10">
          <div className="inline-block merlin-float mb-3">
            <img src={merlinImg} alt="Merlin" className="w-14 h-14 object-contain"
              style={{ filter: 'drop-shadow(0 0 20px rgba(254,130,4,0.45))' }} />
          </div>
          <h2 className="text-2xl font-black leading-tight mb-1" style={{ color: 'rgba(255,255,255,0.95)' }}>
            Débloque le potentiel de Merlin
          </h2>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Change ou annule à tout moment.
          </p>

          {/* Toggle Mensuel / Annuel — annuel par défaut (-30% ancrage économies) */}
          <div className="mt-4 inline-flex items-center gap-1 p-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <button
              type="button"
              onClick={() => setBillingPeriod('monthly')}
              className="px-4 py-1.5 rounded-full text-xs font-bold transition-all"
              style={{
                background: billingPeriod === 'monthly' ? 'rgba(255,255,255,0.95)' : 'transparent',
                color: billingPeriod === 'monthly' ? '#1e1145' : 'rgba(255,255,255,0.6)',
              }}
            >
              Mensuel
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('annual')}
              className="px-4 py-1.5 rounded-full text-xs font-bold transition-all relative"
              style={{
                background: billingPeriod === 'annual' ? 'linear-gradient(135deg, #fe8204, #f59e0b)' : 'transparent',
                color: billingPeriod === 'annual' ? 'white' : 'rgba(255,255,255,0.6)',
                boxShadow: billingPeriod === 'annual' ? '0 2px 8px rgba(254,130,4,0.35)' : 'none',
              }}
            >
              Annuel
              <span
                className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                style={{
                  background: billingPeriod === 'annual' ? 'rgba(255,255,255,0.25)' : 'rgba(34,197,94,0.2)',
                  color: billingPeriod === 'annual' ? 'white' : '#22c55e',
                }}
              >
                -30%
              </span>
            </button>
          </div>

          {/* Bannière 'tarif béta figé à vie' — gros levier loss aversion */}
          {BETA_PRICING_ACTIVE && (
            <div
              className="mt-4 mx-auto max-w-md rounded-xl px-4 py-2.5 text-xs font-semibold flex items-center gap-2 justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(254,130,4,0.18), rgba(245,158,11,0.10))',
                border: '1px solid rgba(254,130,4,0.45)',
                color: '#fdba74',
                boxShadow: '0 4px 18px rgba(254,130,4,0.18)',
              }}
            >
              <span aria-hidden>🔒</span>
              <span>
                <b style={{ color: '#fed7aa' }}>Tarif béta figé à vie.</b>{' '}
                <span style={{ color: 'rgba(254,215,170,0.75)' }}>
                  Ton prix actuel reste le tien tant que tu restes abonné. Après la béta, plus disponible.
                </span>
              </span>
            </div>
          )}
        </div>

        {/* Plans grid — responsive
             pt-4 sur le grid pour que les badges -top-2.5 ('Plan actuel' / 'Populaire')
             ne soient pas tronqués par le clip du container overflow-y-auto */}
        <div className="relative px-5 pb-5 overflow-y-auto z-10"
          style={{ maxHeight: 'calc(92vh - 200px)' }}>
          <div className={`grid grid-cols-1 md:grid-cols-2 ${visiblePlans.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3 pt-4`}>
            {visiblePlans.map(p => {
              const isCurrent = p.id === currentPlan
              const isDowngrade = PLAN_ORDER[p.id] < currentIdx
              const isUpgrade = PLAN_ORDER[p.id] > currentIdx
              const isPaid = p.price > 0
              const isPopular = p.popular

              return (
                <div key={p.id}
                  className="relative rounded-2xl p-4 flex flex-col transition-all"
                  style={{
                    background: isPopular
                      ? 'linear-gradient(135deg, rgba(254,130,4,0.10), rgba(245,158,11,0.05))'
                      : isCurrent
                        ? 'rgba(124,58,237,0.10)'
                        : 'rgba(255,255,255,0.03)',
                    border: `1.5px solid ${isPopular ? 'rgba(254,130,4,0.5)' : isCurrent ? 'rgba(124,58,237,0.35)' : 'rgba(255,255,255,0.08)'}`,
                    boxShadow: isPopular ? '0 8px 32px rgba(254,130,4,0.15)' : 'none',
                  }}>
                  {/* Badge — wrapper flex pour un centrage bulletproof (le -translate-x-1/2 sur inline span
                       pouvait etre inconsistant sur certains rendus, flex justify-center est infaillible) */}
                  {isPopular && !isCurrent && (
                    <div className="absolute -top-2.5 left-0 right-0 flex justify-center pointer-events-none z-10">
                      <span className="text-xs font-bold text-white px-3 py-1 rounded-full whitespace-nowrap"
                        style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)', boxShadow: '0 2px 8px rgba(254,130,4,0.4)' }}>
                        ★ Populaire
                      </span>
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute -top-2.5 left-0 right-0 flex justify-center pointer-events-none z-10">
                      <span className="text-xs font-bold text-white px-3 py-1 rounded-full whitespace-nowrap"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #5b21b6)', boxShadow: '0 2px 8px rgba(124,58,237,0.4)' }}>
                        Plan actuel
                      </span>
                    </div>
                  )}

                  {(() => {
                    // Le prix affiché dépend du toggle billingPeriod
                    const displayPrice = billingPeriod === 'annual' && p.priceAnnualMonthly != null
                      ? p.priceAnnualMonthly
                      : p.price
                    const annualSavings = billingPeriod === 'annual' && p.price > 0 && p.priceAnnualMonthly != null
                      ? Math.round((p.price - p.priceAnnualMonthly) * 12)
                      : 0
                    return (
                      <>
                        <h3 className="text-base font-bold mt-1 flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.95)' }}>
                          {p.label}
                          {BETA_PRICING_ACTIVE && p.price > 0 && p.regularPrice > p.price && (
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                              style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)', color: 'white' }}>
                              -{Math.round(((p.regularPrice - p.price) / p.regularPrice) * 100)}%
                            </span>
                          )}
                        </h3>

                        <div className="mt-2.5 mb-4">
                          {BETA_PRICING_ACTIVE && p.price > 0 && p.regularPrice > p.price && (
                            <div className="flex items-baseline gap-2 mb-0.5">
                              <span className="text-sm line-through" style={{ color: 'rgba(255,255,255,0.35)' }}>
                                {p.regularPrice.toFixed(2).replace('.', ',')}€
                              </span>
                              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#fe8204' }}>
                                Promo beta
                              </span>
                            </div>
                          )}
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-black leading-none"
                              style={{ color: isPopular ? '#fe8204' : 'white' }}>
                              {displayPrice === 0 ? 'Gratuit' : `${Math.floor(displayPrice)}€`}
                            </span>
                            {displayPrice > 0 && displayPrice % 1 > 0 && (
                              <span className="text-sm font-bold" style={{ color: isPopular ? '#fe8204' : 'rgba(255,255,255,0.7)' }}>
                                {String((displayPrice % 1).toFixed(2)).substring(1)}
                              </span>
                            )}
                            {displayPrice > 0 && (
                              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
                                {billingPeriod === 'annual' ? '/mois équivalent' : '/mois'}
                              </span>
                            )}
                          </div>
                          {/* Pour l'annuel : afficher EXPLICITEMENT le total prélevé une fois par an */}
                          {billingPeriod === 'annual' && p.priceAnnualMonthly != null && p.priceAnnualMonthly > 0 && (
                            <div className="mt-1.5 space-y-0.5">
                              <p className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>
                                Soit <b style={{ color: '#fbbf24' }}>{Math.round(p.priceAnnualMonthly * 12)}€ facturés une fois par an</b>
                              </p>
                              {annualSavings > 0 && (
                                <p className="text-[11px] font-bold uppercase tracking-wider inline-block px-2 py-0.5 rounded-full"
                                  style={{ background: 'rgba(34,197,94,0.18)', color: '#86efac' }}>
                                  Économise {annualSavings}€/an
                                </p>
                              )}
                            </div>
                          )}
                          <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                            {p.credits.toLocaleString()} crédits/mois
                          </p>
                          {BETA_PRICING_ACTIVE && p.price > 0 && p.regularPrice > p.price && (
                            <p className="text-xs mt-1 italic" style={{ color: 'rgba(255,255,255,0.4)' }}>
                              Tarif figé tant que tu restes abonné.
                            </p>
                          )}
                        </div>
                      </>
                    )
                  })()}

                  <div className="space-y-2 mb-5 flex-1">
                    {p.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* Action */}
                  {isCurrent ? (
                    <button onClick={isPaid ? handleManageBilling : undefined}
                      disabled={!isPaid || loading === 'portal'}
                      className="w-full py-2.5 text-sm font-bold rounded-xl transition-all disabled:opacity-50"
                      style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.35)', color: '#c084fc' }}>
                      {loading === 'portal' ? '...' : isPaid ? 'Gérer mon abonnement' : 'Plan actuel'}
                    </button>
                  ) : isUpgrade ? (
                    <button onClick={() => handleUpgrade(p.id)}
                      disabled={loading === p.id}
                      className="w-full py-2.5 text-sm font-bold text-white rounded-xl transition-all hover:scale-[1.02] disabled:opacity-60"
                      style={{
                        background: isPopular
                          ? 'linear-gradient(135deg, #fe8204, #f59e0b)'
                          : 'linear-gradient(135deg, #7c3aed, #5b21b6)',
                        boxShadow: isPopular
                          ? '0 4px 16px rgba(254,130,4,0.4)'
                          : '0 4px 16px rgba(124,58,237,0.35)',
                      }}>
                      {loading === p.id ? 'Redirection...' : `Passer à ${p.label}`}
                    </button>
                  ) : isDowngrade ? (
                    <button onClick={handleManageBilling}
                      disabled={loading === 'portal'}
                      className="w-full py-2.5 text-sm font-semibold rounded-xl transition-all hover:bg-white/5"
                      style={{ border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.55)' }}>
                      {loading === 'portal' ? '...' : 'Rétrograder'}
                    </button>
                  ) : null}
                </div>
              )
            })}
          </div>

          {/* Error */}
          {error && (
            <div className="mt-4 p-3 rounded-xl text-sm"
              style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="relative px-6 py-3 flex items-center justify-between text-xs z-10"
          style={{ background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)' }}>
          <span>
            Plan actuel : <strong className="uppercase" style={{ color: 'rgba(255,255,255,0.7)' }}>{currentPlan}</strong>
          </span>
          <span>
            {isUnlimited ? 'Crédits illimités' : `${remainingCredits().toLocaleString()} / ${maxCredits.toLocaleString()} crédits`}
          </span>
        </div>
      </div>
    </div>
  )
}
